import React, {useState} from 'react';
import {
    ActionButton,
    DialogEditActionsWrapper,
    DialogWrapper,
    TextInputShort,
    TextWrapper
} from './CreateFolderSelectionDialog.style';
import {t} from 'i18next';
import {Accordion} from '../../Accordion/Accordion';
import {
    getFontSize,
    isNullOrEmpty,
    isValidText,
    pathToResource,
    SPECIAL_CHARACTERS,
    SPECIAL_END_CHARACTERS,
    validateContainsSpecialCharacter,
    validateEndWithSpecialCharacter
} from '../../../utils';
import {Valid} from '../../validation/Valid';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {CreateFolderDialogProps} from '../StorageObjectDialogProps';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {ObjectType} from '../../../models/ObjectType';
import {STORAGE_OBJECT_PATH} from '../../../init/storage_object_constants';

/**
 * @name CreateFolderSelectionDialog
 * @description
 * The dialog to select a new folder name
 */
export const CreateFolderSelectionDialog: React.VFC<CreateFolderDialogProps> = (props) => {
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FOLDER));
    const [nameInput, setNameInput] = useState('');
    const handleNameInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        setNameInput(eventValue);
    };

    let validatedNameInputSpecialCharacter = isNullOrEmpty(nameInput) ? true : (!validateContainsSpecialCharacter(nameInput));
    let validatedNameInputEndSpecialCharacter = isNullOrEmpty(nameInput) ? true : (!validateEndWithSpecialCharacter(nameInput));
    let validationNameInputMessage = (!validatedNameInputEndSpecialCharacter) ? t('ERROR_FOLDER_NAME_SPECIAL_END_CHARACTER_CONSTRAINT', {param1: SPECIAL_END_CHARACTERS}) : t('ERROR_FOLDER_NAME_SPECIAL_CHARACTER_CONSTRAINT', {param1: SPECIAL_CHARACTERS});

    const validateNameInput = (): boolean => {
        return (isValidText(nameInput) && (nameInput.trim().length > 0) && validatedNameInputSpecialCharacter && validatedNameInputEndSpecialCharacter) as boolean;
    };

    let validatedNameInput = validateNameInput();

    const handleContinue = () => {
        if (validatedNameInput) {
            props.onContinue(nameInput);
        }
    };

    let fontSize = getFontSize([
        t('CREATE_FOLDER_SELECTION_DIALOG_LABEL')
    ], 10);

    return (
        <DialogWrapper id={generateIdentifier()}
                       key={'key'}
                       modal
                       open
                       title={t('CREATE_FOLDER_SELECTION_DIALOG_TITLE')}
                       onClose={props.onClose}>
            <TextWrapper id={generateIdentifier()}>
                <p id={generateIdentifier()}>{t('CREATE_FOLDER_SELECTION_DIALOG_MOVING_FOLDER_WARNING')}</p>
                {isVisibleComponent(STORAGE_OBJECT_PATH, templatePropertySettings) &&
                    <p id={generateIdentifier()}>
                        {t('CREATE_FOLDER_SELECTION_DIALOG_FOLDER_PATH_INFO_TEXT', {path: decodeURI(pathToResource(props.breadcrumbs))})}
                    </p>
                }
            </TextWrapper>
            <Accordion accordionHeadline={t('CREATE_FOLDER_SELECTION_DIALOG_ACCORDION_TITLE_FOLDER_NAME')}
                       startOpen>
                <Valid isValid={validatedNameInputSpecialCharacter && validatedNameInputEndSpecialCharacter}
                       message={validationNameInputMessage}>
                    <TextInputShort id={generateIdentifier('name')}
                                    label={`${t('META_DATA_DIALOG_NAME_LABEL')}*`}
                                    required
                                    autoFocus={true}
                                    value={nameInput}
                                    onChange={handleNameInput}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            handleContinue();
                                        }
                                    }}/>
                </Valid>
            </Accordion>
            <DialogEditActionsWrapper id={generateIdentifier()} className='m-dialog__actions'>
                <ActionButton id={generateIdentifier()}
                              key='create'
                              icon='folder-add'
                              mode='primary'
                              type='button'
                              label={t('CREATE_FOLDER_SELECTION_DIALOG_LABEL')}
                              title={t('CREATE_FOLDER_SELECTION_DIALOG_TITLE')}
                              fontSize={fontSize}
                              disabled={!validatedNameInput}
                              onClick={handleContinue}/>
            </DialogEditActionsWrapper>
        </DialogWrapper>
    );
};
